export type PartStatus = 'Research only' | 'Requested' | 'Ordered';
export type OrderStatus = 'Processed' | 'Delayed' | 'Rejected' | '';

export interface WorkOrderPart {
  status: PartStatus;
  vendor: string;
  vendorPhone: string;
  vendorContact: string;
  description: string;
  partNumber: string;
  price: string;
  dateOrdered: string;
  eta: string;
  poNumber: string;
  orderStatus: OrderStatus;
  shippingAmount: string;
  shippingNote: string;
  otherChargesAmount: string;
  otherChargesNote: string;
  delayReason: string;
  estimatedDateAvailable: string;
  rejectionReason: string;
}

export const PART_STATUSES: PartStatus[] = ['Research only', 'Requested', 'Ordered'];
export const ORDER_STATUSES: Array<Exclude<OrderStatus, ''>> = ['Processed', 'Delayed', 'Rejected'];


/** Format a money amount for display as dollars (e.g. "$12.50"). Empty stays empty. */
export function formatDollars(raw: any): string {
  const s = raw != null ? String(raw).trim() : '';
  if (!s) return '';
  const cleaned = s.replace(/[$,\s]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return s;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return s;
  const sign = n < 0 ? '-' : '';
  return sign + '$' + Math.abs(n).toFixed(2);
}

/** Strip leading $ for easier editing on focus; leave other text intact. */
export function unformatDollarsForEdit(raw: any): string {
  const s = raw != null ? String(raw).trim() : '';
  if (!s) return '';
  return s.replace(/^\$/, '');
}

export function blankWorkOrderPart(): WorkOrderPart {
  return {
    status: 'Research only',
    vendor: '',
    vendorPhone: '',
    vendorContact: '',
    description: '',
    partNumber: '',
    price: '',
    dateOrdered: '',
    eta: '',
    poNumber: '',
    orderStatus: '',
    shippingAmount: '',
    shippingNote: '',
    otherChargesAmount: '',
    otherChargesNote: '',
    delayReason: '',
    estimatedDateAvailable: '',
    rejectionReason: '',
  };
}

export function normalizeWorkOrderPart(raw: any): WorkOrderPart {
  const p = raw && typeof raw === 'object' ? raw : {};
  let status = typeof p.status === 'string' ? p.status : '';
  if (PART_STATUSES.indexOf(status as PartStatus) === -1) {
    // Legacy parts were partNumber + price only; treat as Research only.
    status = 'Research only';
  }

  // Prefer orderStatus; fall back to legacy orderedOutcome; empty ? Processed when Ordered.
  let orderStatus = typeof p.orderStatus === 'string' ? p.orderStatus : '';
  if (ORDER_STATUSES.indexOf(orderStatus as any) === -1) {
    const legacy = typeof p.orderedOutcome === 'string' ? p.orderedOutcome : '';
    if (legacy === 'Delayed' || legacy === 'Rejected') {
      orderStatus = legacy;
    } else {
      orderStatus = '';
    }
  }
  if (status !== 'Ordered') {
    orderStatus = '';
  } else if (ORDER_STATUSES.indexOf(orderStatus as any) === -1) {
    orderStatus = 'Processed';
  }

  return {
    status: status as PartStatus,
    vendor: p.vendor != null ? String(p.vendor) : '',
    vendorPhone: p.vendorPhone != null ? String(p.vendorPhone) : '',
    vendorContact: p.vendorContact != null ? String(p.vendorContact) : '',
    description: p.description != null ? String(p.description) : '',
    partNumber: p.partNumber != null ? String(p.partNumber) : '',
    price: formatDollars(p.price != null ? String(p.price) : ''),
    dateOrdered: p.dateOrdered != null ? String(p.dateOrdered) : '',
    eta: p.eta != null ? String(p.eta) : '',
    poNumber: p.poNumber != null ? String(p.poNumber) : '',
    orderStatus: orderStatus as OrderStatus,
    shippingAmount: formatDollars(p.shippingAmount != null ? String(p.shippingAmount) : ''),
    shippingNote: p.shippingNote != null ? String(p.shippingNote) : '',
    otherChargesAmount: formatDollars(p.otherChargesAmount != null ? String(p.otherChargesAmount) : ''),
    otherChargesNote: p.otherChargesNote != null ? String(p.otherChargesNote) : '',
    delayReason: p.delayReason != null ? String(p.delayReason) : '',
    estimatedDateAvailable: p.estimatedDateAvailable != null ? String(p.estimatedDateAvailable) : '',
    rejectionReason: p.rejectionReason != null ? String(p.rejectionReason) : '',
  };
}

export function normalizeWorkOrderParts(parts: any): WorkOrderPart[] {
  if (!Array.isArray(parts)) return [];
  return parts.map(normalizeWorkOrderPart);
}

/** Returns an error message, or null if valid. */
export function validateWorkOrderParts(parts: WorkOrderPart[]): string | null {
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.status === 'Ordered' && p.orderStatus === 'Delayed') {
      if (!String(p.delayReason || '').trim()) {
        return `Part ${i + 1}: Delay reason is required when Order status is Delayed.`;
      }
    }
    if (p.status === 'Ordered' && p.orderStatus === 'Rejected') {
      if (!String(p.rejectionReason || '').trim()) {
        return `Part ${i + 1}: Rejection reason is required when Order status is Rejected.`;
      }
    }
  }
  return null;
}